import type { RetailerKey } from '@vizit/constants';
import type { ScoreLevel } from '@vizit/organisms';
import * as React from 'react';
import { ProductTableFilterOptions } from '../../components';
import { ProductTableContext } from './product-table-context';
import {
  DEFAULT_FILTER_STATE,
  ProductTableActionType,
  type ProductTableState,
  productTableFilterReducer,
} from './product-table-provider.state';

interface ProductTableProviderProps extends React.PropsWithChildren, ProductTableState {
  /**
   * The ID of the organization to fetch products for
   *
   * @required
   * @type {string}
   */
  organizationId: string;
  /**
   * The ID of the portfolio to fetch products for
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * The function to call when the product table filters change
   *
   * @optional
   * @type {(filter: ProductTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onProductTableFilterChange?: (filter: ProductTableFilterOptions, value?: string) => void;
}

/**
 * Provider component for the product table context.
 * Manages the state for product table filters, sorting, and pagination.
 *
 * @param props - The provider props
 */
function ProductTableProvider(props: ProductTableProviderProps) {
  const {
    children,
    portfolioId,
    organizationId,
    limit,
    sortBy,
    sortDirection,
    brand,
    productCategoryId,
    productCategoryName,
    listingScoreLevel,
    searchTerm,
    onProductTableFilterChange,
  } = props;

  const [filterState, dispatch] = React.useReducer(productTableFilterReducer, {
    limit: limit ?? DEFAULT_FILTER_STATE.limit,
    sortBy: sortBy ?? DEFAULT_FILTER_STATE.sortBy,
    sortDirection: sortDirection ?? DEFAULT_FILTER_STATE.sortDirection,
    brand: brand ?? DEFAULT_FILTER_STATE.brand,
    productCategoryId: productCategoryId ?? DEFAULT_FILTER_STATE.productCategoryId,
    productCategoryName: productCategoryName ?? DEFAULT_FILTER_STATE.productCategoryName,
    listingScoreLevel: listingScoreLevel ?? DEFAULT_FILTER_STATE.listingScoreLevel,
    searchTerm: searchTerm ?? DEFAULT_FILTER_STATE.searchTerm,
    retailer: props.retailer ?? DEFAULT_FILTER_STATE.retailer,
  });

  const handleOnFilterBrand = React.useCallback(
    (brand: string) => {
      dispatch({ type: ProductTableActionType.SET_BRAND, payload: { brand } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Brand, brand);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterProductCategory = React.useCallback(
    (productCategoryId: string, productCategoryName: string) => {
      dispatch({
        type: ProductTableActionType.SET_PRODUCT_CATEGORY_ID,
        payload: { productCategoryId, productCategoryName },
      });

      onProductTableFilterChange?.(ProductTableFilterOptions.ProductCategory, productCategoryId);
      onProductTableFilterChange?.(ProductTableFilterOptions.ProductCategoryName, productCategoryName);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterListingScoreLevel = React.useCallback(
    (listingScoreLevel: ScoreLevel) => {
      dispatch({ type: ProductTableActionType.SET_LISTING_SCORE_LEVEL, payload: { listingScoreLevel } });

      onProductTableFilterChange?.(ProductTableFilterOptions.ListingScoreLevel, listingScoreLevel);
    },
    [onProductTableFilterChange],
  );

  const handleOnSearchTermChange = React.useCallback(
    (value: string) => {
      dispatch({ type: ProductTableActionType.SET_SEARCH_TERM, payload: { searchTerm: value } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Search, value);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterRetailer = React.useCallback(
    (retailer: RetailerKey | undefined) => {
      dispatch({ type: ProductTableActionType.SET_RETAILER, payload: { retailer } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Retailer, retailer);
    },
    [onProductTableFilterChange],
  );

  return (
    <ProductTableContext.Provider
      value={{
        ...filterState,
        portfolioId,
        organizationId,
        onFilterBrand: handleOnFilterBrand,
        onFilterProductCategory: handleOnFilterProductCategory,
        onFilterListingScoreLevel: handleOnFilterListingScoreLevel,
        onSearchTermChange: handleOnSearchTermChange,
        onFilterRetailer: handleOnFilterRetailer,
      }}
    >
      {children}
    </ProductTableContext.Provider>
  );
}

/**
 * Hook to access the product table context.
 * Must be used within a ProductTableProvider.
 *
 * @throws Error if used outside of ProductTableProvider
 * @returns The product table context
 */
const useProductTable = () => {
  const context = React.useContext(ProductTableContext);

  if (!context) {
    throw new Error('useProductTable must be used within a ProductTableProvider');
  }

  return context;
};

export { ProductTableProvider, useProductTable };
